import * as React from 'react';
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import {
  ArrowsUpDownIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/solid';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SelectionActionBar } from '@/components/ui/selection-action-bar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { PageHeader } from '@/components/ui/page-header';
import { useAgents } from '@/lib/hooks/useAgents';
import type { Agent } from '@/lib/services/agents';

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace('.0', '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(0) + 'K';
  }
  return num.toString();
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export default function AgentsPage() {
  const navigate = useNavigate();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const { agents = [], deleteAgent, toggleAgentStatus, isLoading } = useAgents();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const handleToggleStatus = async (agent: Agent) => {
    try {
      await toggleAgentStatus(agent.id, agent.status);
    } catch (error) {
      console.error('Error updating agent:', error);
    }
  };

  const handleDeleteAgent = async (agentId: string) => {
    if (!confirm('Are you sure you want to delete this agent?')) return;

    try {
      await deleteAgent(agentId);
    } catch (error) {
      console.error('Error deleting agent:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAgentIds.length === 0) return;

    setIsDeleting(true);
    try {
      await Promise.all(
        selectedAgentIds.map((id) => deleteAgent(id))
      );
      setRowSelection({});
      setIsDeleteDialogOpen(false);
      setSelectedAgentIds([]);
      toast.success(`Successfully deleted ${selectedAgentIds.length} agent${selectedAgentIds.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error deleting agents:', error);
      toast.error('Failed to delete some agents');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: ColumnDef<Agent, any>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <div className="flex items-center h-full">
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center h-full">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'websiteUrl',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="!p-0 h-auto font-normal hover:text-foreground hover:bg-transparent"
        >
          Website
          <ArrowsUpDownIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div
          className="font-normal truncate max-w-[200px]"
        >
          {row.getValue('websiteUrl')}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const agent = row.original;
        const isActive = agent.status === 'active';

        return (
          <Switch
            checked={isActive}
            onCheckedChange={() => handleToggleStatus(agent)}
          />
        );
      },
    },
    {
      accessorKey: 'leadsFound',
      header: () => <div className="text-right">Leads Found</div>,
      cell: ({ row }) => {
        const count = row.getValue('leadsFound') as number;
        return <div className="text-right font-normal">{formatNumber(count || 0)}</div>;
      },
    },
    {
      accessorKey: 'messagesGenerated',
      header: () => <div className="text-right">Messages</div>,
      cell: ({ row }) => {
        const count = row.getValue('messagesGenerated') as number;
        return <div className="text-right font-normal">{formatNumber(count || 0)}</div>;
      },
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="!p-0 h-auto font-normal hover:text-foreground hover:bg-transparent"
        >
          Created
          <ArrowsUpDownIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="text-muted-foreground">
          {formatDate(row.getValue('createdAt'))}
        </div>
      ),
    },
    {
      id: 'actions',
      enableHiding: false,
      header: () => <div className="text-right mr-4">Actions</div>,
      cell: ({ row }) => {
        const agent = row.original;
        return (
          <div className="flex justify-end mr-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" className="h-10 w-10 p-0 has-[>svg]:px-0 bg-card">
                  <span className="sr-only">Open menu</span>
                  <EllipsisHorizontalIcon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-danger-foreground"
                  onClick={() => handleDeleteAgent(agent.id)}
                >
                  Delete agent
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: agents,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const handleOpenDeleteDialog = () => {
    const selectedRows = table.getFilteredSelectedRowModel().rows;
    const ids = selectedRows.map(row => row.original.id);
    setSelectedAgentIds(ids);
    setIsDeleteDialogOpen(true);
  };

  return (
    <>
      <PageHeader title="Agents" className="mt-page-top mb-header-gap">
        <Input
          placeholder="Search agents"
          value={(table.getColumn('websiteUrl')?.getFilterValue() as string) ?? ''}
          onChange={(event) =>
            table.getColumn('websiteUrl')?.setFilterValue(event.target.value)
          }
          className="max-w-sm w-70"
          prefixIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
        />
        <Button variant="primary" onClick={() => navigate('/dashboard/agents/new')}>
          Create a new agent
        </Button>
      </PageHeader>

      <div className="w-full">
        {isLoading ? (
          <>
            <div className="relative">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <div className="flex items-center h-full">
                        <Skeleton className="h-5 w-5 rounded-full ml-2" />
                      </div>
                    </TableHead>
                    <TableHead><Skeleton className="h-4 w-12" /></TableHead>
                    <TableHead><Skeleton className="h-4 w-16" /></TableHead>
                    <TableHead><Skeleton className="h-4 w-12" /></TableHead>
                    <TableHead className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableHead>
                    <TableHead className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableHead>
                    <TableHead><Skeleton className="h-4 w-14" /></TableHead>
                    <TableHead>
                      <div className="flex justify-end mr-4">
                        <Skeleton className="h-4 w-12" />
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="flex items-center h-full">
                          <Skeleton className="h-5 w-5 rounded-full ml-2" />
                        </div>
                      </TableCell>
                      <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-11" /></TableCell>
                      <TableCell className="text-right"><Skeleton className="h-4 w-8 ml-auto" /></TableCell>
                      <TableCell className="text-right"><Skeleton className="h-4 w-8 ml-auto" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      <TableCell>
                        <div className="flex justify-end">
                          <Skeleton className="h-8 w-8 mr-4" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        ) : (
          <>
            <div className="relative" ref={tableContainerRef} style={{ contain: 'layout paint' }}>
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-row-id={row.id}
                        data-state={row.getIsSelected() && 'selected'}
                        className="cursor-pointer"
                        onClick={() => navigate(`/dashboard/agents/${row.original.id}`)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        No agents yet. Create your first agent to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <SelectionActionBar
              selectedCount={table.getFilteredSelectedRowModel().rows.length}
              isVisible={table.getFilteredSelectedRowModel().rows.length > 0}
              onClearSelection={() => setRowSelection({})}
              itemName={{ singular: 'agent', plural: 'agents' }}
              tableContainerRef={tableContainerRef}
              selectedRowIds={table.getFilteredSelectedRowModel().rows.map(row => row.id)}
            >
              <Button
                variant="destructive"
                size="xs"
                onClick={handleOpenDeleteDialog}
              >
                Delete
              </Button>
            </SelectionActionBar>

            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Delete Agents</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <p className="text-sm text-muted-foreground">
                    Are you sure you want to delete {selectedAgentIds.length} agent{selectedAgentIds.length > 1 ? 's' : ''}? This action cannot be undone.
                  </p>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="secondary"
                    onClick={() => setIsDeleteDialogOpen(false)}
                    disabled={isDeleting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleBulkDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {table.getPageCount() > 1 && (
              <div className="flex items-center justify-end space-x-2 py-4 ml-8">
                <div className="space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
